/**
 * HoldingsPage — Sprint 21.6d
 *
 * Layout top → bottom:
 *   1. Header
 *   2. Alerts Strip (concentration + sync + paper-active + unclassified)
 *   3. Total NAV hero
 *   4. Accounts strip (corrected ordering: Kamal → Binance → algo subs)
 *   5. Account filter bar
 *   6. Per-account sections:
 *      - Open Positions table (qty>0 only)
 *      - Collapsible Trade History with 30d/90d/1y/3y/all window selector
 *   7. Realized Performance section (scope + window filters, hit rate,
 *      expectancy, YoY, per-account breakdown, monthly bar chart)
 *
 * P17 still applies — no dollar amounts leave this page.
 */

import { useEffect, useState, useMemo } from 'react';
import { Lock, Unlock, RefreshCw, AlertTriangle, Wallet, Eye, Info, XCircle, CheckCircle, ChevronDown, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const UNLOCK_FLAG = 'korinth_plutus_unlocked';

// Sprint 21.6e: priority keyed off the IBKR alias (external_account_id
// now stores the alias after the parser rewrite). Numeric IDs were wrong.
const ACCOUNT_PRIORITY = {
  'Kamal':            1,
  'binance-spot':     2,
  'Intraday Momentum': 3,
  'ML Breakout':      4,
  'Mean Reversion':   5,
  'Micro Scalper':    6,
};

function accountOrder(externalId) {
  if (!externalId) return 99;
  return ACCOUNT_PRIORITY[externalId] ?? 99;
}

// Primary card name = the IBKR alias (external_account_id).
// "Kamal M Ali" (account holder legal name) must NEVER appear.
function accountLabel(account) {
  return account.external_account_id || account.account_name || account.broker;
}

const PURPOSE_LABELS = {
  discretionary: 'Discretionary',
  algo_paper:    'Algo (paper)',
  algo_live:     'Algo (live)',
  crypto_spot:   'Crypto Spot',
  retirement:    'Retirement',
  unclassified:  'Unclassified',
};
const PURPOSE_COLOR = {
  discretionary: '#10b981',  // green
  algo_paper:    '#3b82f6',  // blue (Sprint 21.6e: changed from amber)
  algo_live:     '#f97316',  // orange (Sprint 21.6e: changed from red)
  crypto_spot:   '#8b5cf6',  // purple (Sprint 21.6e: changed from blue)
  retirement:    '#6b7280',  // gray
  unclassified:  '#6b7280',  // muted gray (Sprint 21.6e: no "ALGO" fallback)
};

function fmtUsd(n) { if (n == null || !Number.isFinite(n)) return '—'; return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }
function fmtUsdP(n) { if (n == null || !Number.isFinite(n)) return '—'; return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`; }
function fmtPct(n) { if (n == null || !Number.isFinite(n)) return '—'; return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`; }
function fmtDate(raw) {
  if (!raw) return '—';
  const d = typeof raw === 'number' ? new Date(raw) : new Date(String(raw));
  if (isNaN(d.getTime())) return String(raw).slice(0, 10);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${d.getUTCFullYear()}`;
}

// ─── PIN Gate (unchanged) ──────────────────────────────────────────────

function PinGate({ hasPinSet, onUnlocked }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [setupMode] = useState(!hasPinSet);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      if (setupMode) {
        if (newPin !== confirmPin) { setError('PINs do not match'); setSubmitting(false); return; }
        if (!/^\d{6}$/.test(newPin)) { setError('PIN must be exactly 6 digits'); setSubmitting(false); return; }
        const r = await fetch('/api/plutus/set-pin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ new_pin: newPin }) });
        const data = await r.json();
        if (!r.ok) { setError(data.error || 'Failed'); setSubmitting(false); return; }
        sessionStorage.setItem(UNLOCK_FLAG, 'true'); onUnlocked();
      } else {
        const r = await fetch('/api/plutus/unlock', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pin }) });
        const data = await r.json();
        if (!r.ok) {
          if (data.reason === 'locked') setError(`Locked out. Try again in ${Math.ceil((data.seconds_remaining || 0) / 60)} min.`);
          else if (data.reason === 'wrong_pin') setError(`Incorrect PIN. ${data.attempts_remaining} attempts remaining.`);
          else setError(data.error || 'Unlock failed');
          setSubmitting(false); return;
        }
        sessionStorage.setItem(UNLOCK_FLAG, 'true'); onUnlocked();
      }
    } catch (e) { setError(e.message); setSubmitting(false); }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-6 max-w-sm w-full">
        <div className="flex items-center gap-2 mb-4"><Lock className="w-5 h-5 text-korinth-accent" /><span className="text-lg font-mono font-bold text-white">PLUTUS · Private Layer</span></div>
        <p className="text-[11px] font-mono text-korinth-muted leading-relaxed mb-4">
          {setupMode ? 'Set a 6-digit PIN to unlock holdings. PLUTUS encrypts all dollar-valued fields at rest. Lose the PIN, lose access — there\'s no recovery.' : 'Enter your 6-digit PIN to view holdings. 5 failed attempts = 15-min lockout.'}
        </p>
        <form onSubmit={submit} className="space-y-3">
          {setupMode ? (
            <>
              <input type="password" inputMode="numeric" maxLength={6} autoFocus placeholder="New 6-digit PIN" value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} className="w-full bg-korinth-bg border border-korinth-border rounded px-3 py-2 text-center text-lg tracking-[0.5em] font-mono text-white" />
              <input type="password" inputMode="numeric" maxLength={6} placeholder="Confirm PIN" value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))} className="w-full bg-korinth-bg border border-korinth-border rounded px-3 py-2 text-center text-lg tracking-[0.5em] font-mono text-white" />
            </>
          ) : (
            <input type="password" inputMode="numeric" maxLength={6} autoFocus placeholder="6-digit PIN" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} className="w-full bg-korinth-bg border border-korinth-border rounded px-3 py-2 text-center text-lg tracking-[0.5em] font-mono text-white" />
          )}
          {error && <div className="text-[11px] font-mono text-korinth-danger">{error}</div>}
          <button type="submit" disabled={submitting} className="w-full text-xs font-mono font-bold px-3 py-2 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20 disabled:opacity-50">
            {submitting ? 'Unlocking...' : setupMode ? 'Set PIN & Unlock' : 'Unlock'}
          </button>
        </form>
        <div className="text-[9px] font-mono text-korinth-muted/60 mt-4 leading-relaxed">Session stays unlocked for 30 min. P17 — dollar amounts never leave this page.</div>
      </div>
    </div>
  );
}

// ─── Alerts Strip (extended for 21.6d info pills) ──────────────────────

const SEVERITY_META = {
  error:   { color: '#ef4444', prefix: '❌' },
  warning: { color: '#f59e0b', prefix: '⚠️' },
  info:    { color: '#3b82f6', prefix: 'ℹ️' },
  ok:      { color: '#10b981', prefix: '✅' },
};

function AlertsStrip({ alerts }) {
  const items = alerts && alerts.length > 0 ? alerts : [{ severity: 'ok', message: 'All accounts synced · no concentration breaches' }];
  return (
    <div className="flex gap-2 overflow-x-auto py-1 -my-1">
      {items.map((a, i) => {
        const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
        return (
          <div key={i}
            className="flex-shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-mono border whitespace-nowrap"
            style={{ backgroundColor: meta.color + '12', borderColor: meta.color + '50', color: meta.color }}
            title={a.detail || ''}>
            <span>{meta.prefix}</span>
            <span className="font-bold">{a.message}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── AccountCard (with purpose pill) ───────────────────────────────────

function AccountCard({ account, nav, onSync, syncing }) {
  const isBinance = account.broker === 'Binance';
  const cashLabel = isBinance ? 'Stablecoins' : 'Cash';
  const label = accountLabel(account);
  const purpose = account.account_purpose || 'unclassified';
  const purposeColor = PURPOSE_COLOR[purpose];
  const noQueryConfigured = account.sync_status === 'no_query_configured';

  return (
    <div className={`bg-korinth-bg rounded p-3 border border-korinth-border/30 ${noQueryConfigured ? 'opacity-75' : ''}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-xs font-mono font-bold text-white">{account.broker}</span>
        <span className="text-[9px] font-mono text-korinth-accent uppercase tracking-wider px-1.5 py-0.5 rounded bg-korinth-accent/10">{label}</span>
        <span className="text-[8px] font-mono font-bold uppercase px-1 py-0.5 rounded" style={{ color: purposeColor, backgroundColor: purposeColor + '15' }}>
          {PURPOSE_LABELS[purpose]}
        </span>
        <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${account.sync_status === 'ok' ? 'bg-korinth-accent/15 text-korinth-accent' : account.sync_status === 'error' ? 'bg-korinth-danger/15 text-korinth-danger' : noQueryConfigured ? 'bg-korinth-muted/15 text-korinth-muted' : 'bg-korinth-muted/15 text-korinth-muted'}`}
          title={noQueryConfigured ? 'When this algo goes live, add IBKR_FLEX_QUERY_ID_<NAME> to /opt/korinth/backend/.env and PLUTUS will auto-discover it.' : ''}>
          {noQueryConfigured ? 'no Flex query' : (account.sync_status || 'pending')}
        </span>
      </div>

      {noQueryConfigured ? (
        <>
          <div className="text-lg font-mono font-bold text-korinth-muted">—</div>
          <div className="text-[9px] font-mono text-korinth-muted italic">No activity yet</div>
        </>
      ) : (
        <>
          <div className="text-lg font-mono font-bold text-white">{fmtUsd(nav?.nav_usd)}</div>
          <div className="text-[9px] font-mono text-korinth-muted">{cashLabel} {fmtUsd(nav?.cash_usd)} · last sync {account.last_sync_at?.slice(5, 16) || '—'}</div>
        </>
      )}

      <button
        onClick={() => onSync(account.broker.toLowerCase())}
        disabled={noQueryConfigured || syncing === account.broker.toLowerCase()}
        title={noQueryConfigured ? 'Add IBKR_FLEX_QUERY_ID_<NAME> to backend/.env to enable sync' : ''}
        className="mt-2 flex items-center gap-1 text-[9px] font-mono px-2 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20 disabled:opacity-40 disabled:cursor-not-allowed">
        <RefreshCw className={`w-2.5 h-2.5 ${syncing === account.broker.toLowerCase() ? 'animate-spin' : ''}`} />
        {noQueryConfigured ? 'No Flex query' : syncing === account.broker.toLowerCase() ? 'Syncing...' : 'Sync now'}
      </button>
    </div>
  );
}

// ─── Positions table (qty > 0 only) ────────────────────────────────────

function PositionsTable({ positions, account }) {
  const open = positions.filter(p => (p.quantity || 0) > 0);
  if (open.length === 0) return <div className="text-[11px] font-mono text-korinth-muted italic text-center py-3">No open positions in {accountLabel(account)}.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] font-mono">
        <thead>
          <tr className="text-korinth-muted border-b border-korinth-border/30">
            <th className="text-left py-1 px-2">Symbol</th><th className="text-right py-1 px-2">Qty</th><th className="text-right py-1 px-2">Avg Cost</th><th className="text-right py-1 px-2">Mark</th><th className="text-right py-1 px-2">Mkt Value</th><th className="text-right py-1 px-2">P&L $</th><th className="text-right py-1 px-2">P&L %</th><th className="text-right py-1 px-2">Weight</th>
          </tr>
        </thead>
        <tbody>
          {open.map(p => (
            <tr key={p.id} className="border-b border-korinth-border/10 hover:bg-korinth-bg/30">
              <td className="py-1.5 px-2 text-white font-bold">{p.symbol}</td>
              <td className="py-1.5 px-2 text-right text-korinth-text">{p.quantity?.toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
              <td className="py-1.5 px-2 text-right text-korinth-text">{p.avg_cost != null ? `$${p.avg_cost.toFixed(2)}` : '—'}</td>
              <td className="py-1.5 px-2 text-right text-korinth-text">{p.mark_price != null ? `$${p.mark_price.toFixed(2)}` : '—'}</td>
              <td className="py-1.5 px-2 text-right text-white">{fmtUsd(p.market_value)}</td>
              <td className={`py-1.5 px-2 text-right ${(p.unrealized_pnl || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{p.unrealized_pnl != null ? fmtUsdP(p.unrealized_pnl) : '—'}</td>
              <td className={`py-1.5 px-2 text-right ${(p.unrealized_pnl_pct || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{fmtPct(p.unrealized_pnl_pct)}</td>
              <td className="py-1.5 px-2 text-right text-korinth-accent font-bold">{p.portfolio_weight_pct?.toFixed(1) ?? '—'}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Trade History (collapsible, per-account window selector) ──────────

const TRADE_WINDOW_OPTIONS = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y',  label: '1y'  },
  { value: '3y',  label: '3y'  },
  { value: 'all', label: 'All' },
];

function windowCutoffMs(window) {
  const now = Date.now();
  if (window === '30d')  return now - 30  * 86400000;
  if (window === '90d')  return now - 90  * 86400000;
  if (window === '1y')   return now - 365 * 86400000;
  if (window === '3y')   return now - 3 * 365 * 86400000;
  return 0; // 'all'
}

function TradeHistoryBlock({ accountId, trades: overviewTrades }) {
  const stateKey = `korinth_plutus_trade_${accountId}`;
  const initial = (() => {
    try { return JSON.parse(sessionStorage.getItem(stateKey)) || { expanded: false, window: '1y' }; }
    catch { return { expanded: false, window: '1y' }; }
  })();
  const [expanded, setExpanded] = useState(initial.expanded);
  const [window, setWindow] = useState(initial.window || '1y');
  const [fetchedTrades, setFetchedTrades] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    try { sessionStorage.setItem(stateKey, JSON.stringify({ expanded, window })); } catch {}
  }, [expanded, window, stateKey]);

  // Fetch per-account trades when the window exceeds what overview returned (1y).
  // For 30d / 90d / 1y we can just client-filter overviewTrades. For 3y / all,
  // re-fetch from the backend since overview only includes 1y.
  useEffect(() => {
    if (!expanded) return;
    if (window === '3y' || window === 'all') {
      setFetching(true);
      fetch(`/api/plutus/account/${accountId}/trades?window=${window}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.trades) setFetchedTrades(d.trades); })
        .finally(() => setFetching(false));
    } else {
      setFetchedTrades(null); // Use overviewTrades + client filter
    }
  }, [expanded, window, accountId]);

  const sourceTrades = fetchedTrades || overviewTrades;
  const cutoff = windowCutoffMs(window);
  const windowTrades = useMemo(() => sourceTrades.filter(t => {
    const ts = new Date(t.trade_date || 0).getTime();
    return ts >= cutoff;
  }), [sourceTrades, cutoff]);
  const windowRealized = windowTrades.reduce((s, t) => s + (t.realized_pnl || 0), 0);
  const windowRealizedHasAny = windowTrades.some(t => t.realized_pnl != null);

  const WINDOW_LABEL = { '30d': 'Last 30 days', '90d': 'Last 90 days', '1y': 'Last 12 months', '3y': 'Last 3 years', 'all': 'All time' };

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center gap-2 flex-wrap py-1">
        <button onClick={() => setExpanded(e => !e)} className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-text hover:text-white">
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <span className="font-bold uppercase tracking-wider text-[9px]">Trade History · {WINDOW_LABEL[window]}</span>
        </button>
        <select value={window} onChange={e => setWindow(e.target.value)} onClick={e => e.stopPropagation()}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-0.5 text-[9px] font-mono text-white">
          {TRADE_WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="text-[9px] font-mono text-korinth-muted">{windowTrades.length} trade{windowTrades.length === 1 ? '' : 's'}</span>
        {windowRealizedHasAny && (
          <span className="text-[9px] font-mono ml-auto">
            Realized P&L this window: <span className={windowRealized >= 0 ? 'text-korinth-accent font-bold' : 'text-korinth-danger font-bold'}>{fmtUsdP(windowRealized)}</span>
          </span>
        )}
      </div>

      {expanded && (
        fetching ? (
          <div className="text-[11px] font-mono text-korinth-muted italic text-center py-3">Loading {WINDOW_LABEL[window].toLowerCase()}...</div>
        ) : windowTrades.length === 0 ? (
          <div className="text-[11px] font-mono text-korinth-muted italic text-center py-3">No trades in {WINDOW_LABEL[window].toLowerCase()}.</div>
        ) : (
          <div className="overflow-x-auto max-h-96 border border-korinth-border/30 rounded mt-1">
            <table className="w-full text-[10px] font-mono">
              <thead>
                <tr className="text-korinth-muted border-b border-korinth-border/30 sticky top-0 bg-korinth-surface">
                  <th className="text-left py-1 px-2">Date</th><th className="text-left py-1 px-2">Symbol</th><th className="text-left py-1 px-2">Side</th><th className="text-right py-1 px-2">Qty</th><th className="text-right py-1 px-2">Price</th><th className="text-right py-1 px-2">Value</th><th className="text-right py-1 px-2">Realized P&L</th>
                </tr>
              </thead>
              <tbody>
                {windowTrades.map(t => (
                  <tr key={t.id} className="border-b border-korinth-border/10">
                    <td className="py-1 px-2 text-korinth-muted whitespace-nowrap">{fmtDate(t.trade_date)}</td>
                    <td className="py-1 px-2 text-white font-bold">{t.symbol}</td>
                    <td className={`py-1 px-2 font-bold ${t.side === 'BUY' ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{t.side}</td>
                    <td className="py-1 px-2 text-right text-korinth-text">{t.quantity?.toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                    <td className="py-1 px-2 text-right text-korinth-text">{t.price != null ? `$${t.price.toFixed(2)}` : '—'}</td>
                    <td className="py-1 px-2 text-right text-korinth-text">{fmtUsd(t.value_usd)}</td>
                    <td className={`py-1 px-2 text-right ${(t.realized_pnl || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{t.realized_pnl != null ? fmtUsdP(t.realized_pnl) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function AccountSection({ group }) {
  const { account, positions, trades, nav } = group;
  const label = accountLabel(account);
  const openCount = positions.filter(p => (p.quantity || 0) > 0).length;
  return (
    <ModuleContainer
      title={<span className="flex items-center gap-2"><span>{account.broker}</span><span className="text-korinth-muted text-xs">·</span><span>{label}</span></span>}
      subtitle={`NAV ${fmtUsd(nav?.nav_usd)} · ${group.cashLabel} ${fmtUsd(nav?.cash_usd)} · ${openCount} open positions`}
    >
      <div className="space-y-3">
        <div>
          <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Open Positions ({openCount})</div>
          <PositionsTable positions={positions} account={account} />
        </div>
        <TradeHistoryBlock accountId={account.id} trades={trades} />
      </div>
    </ModuleContainer>
  );
}

// ─── Realized Performance section (rich stats, Sprint 21.6d Issue 4) ───

const SCOPE_OPTIONS = [
  { value: 'discretionary', label: 'Discretionary only' },
  { value: 'all',           label: 'All accounts' },
  { value: 'algo_paper',    label: 'Paper/algo only' },
  { value: 'algo_live',     label: 'Live algo only' },
  { value: 'crypto_spot',   label: 'Crypto only' },
];
const RP_WINDOW_OPTIONS = [
  { value: 'ytd',      label: 'YTD' },
  { value: '1y',       label: '1y' },
  { value: '3y',       label: '3y' },
  { value: 'lifetime', label: 'Lifetime' },
];

function withinWindow(dateIso, window) {
  if (!dateIso) return false;
  if (window === 'lifetime') return true;
  const t = new Date(dateIso).getTime();
  const now = Date.now();
  if (window === 'ytd') {
    const yStart = new Date(Date.UTC(new Date(now).getUTCFullYear(), 0, 1)).getTime();
    return t >= yStart;
  }
  if (window === '1y') return t >= now - 365 * 86400000;
  if (window === '3y') return t >= now - 3 * 365 * 86400000;
  return true;
}

function RealizedPerformance({ accounts, realizedTrades }) {
  // Sprint 21.6e: smart default scope — Discretionary only if at least one
  // discretionary account has resolved trades, else All accounts.
  const defaultScope = useMemo(() => {
    const discretionaryIds = new Set(accounts.filter(a => a.account_purpose === 'discretionary').map(a => a.id));
    if (discretionaryIds.size === 0) return 'all';
    const hasDiscretionaryTrades = realizedTrades.some(t => discretionaryIds.has(t.account_id));
    return hasDiscretionaryTrades ? 'discretionary' : 'all';
  }, [accounts, realizedTrades]);

  const [scope, setScope] = useState(defaultScope);
  const [window, setWindow] = useState('ytd');

  // Re-default if accounts/trades change and current scope would yield zero
  useEffect(() => { setScope(defaultScope); }, [defaultScope]);

  // Build account id set for this scope
  const accountsByScope = useMemo(() => {
    const ids = new Set();
    for (const acc of accounts) {
      const p = acc.account_purpose || 'unclassified';
      if (scope === 'all') { ids.add(acc.id); continue; }
      if (scope === p) ids.add(acc.id);
    }
    return ids;
  }, [accounts, scope]);

  const accountById = useMemo(() => Object.fromEntries(accounts.map(a => [a.id, a])), [accounts]);

  // Filter realized trades by scope + window
  const filtered = useMemo(() => realizedTrades.filter(t => accountsByScope.has(t.account_id) && withinWindow(t.trade_date, window)), [realizedTrades, accountsByScope, window]);

  // Stats
  const stats = useMemo(() => {
    const total = filtered.reduce((s, t) => s + (t.realized_pnl || 0), 0);
    const wins = filtered.filter(t => (t.realized_pnl || 0) > 0);
    const losses = filtered.filter(t => (t.realized_pnl || 0) < 0);
    const scored = wins.length + losses.length;
    const hitRate = scored > 0 ? wins.length / scored : null;
    const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.realized_pnl, 0) / wins.length : null;
    const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + t.realized_pnl, 0) / losses.length : null;
    const expectancy = (hitRate != null && avgWin != null && avgLoss != null)
      ? hitRate * avgWin + (1 - hitRate) * avgLoss
      : null;
    const sortedByPnl = [...filtered].sort((a, b) => (b.realized_pnl || 0) - (a.realized_pnl || 0));
    const best = sortedByPnl[0] || null;
    const worst = sortedByPnl[sortedByPnl.length - 1] || null;
    return { total, wins: wins.length, losses: losses.length, scored, hitRate, avgWin, avgLoss, expectancy, best, worst, count: filtered.length };
  }, [filtered]);

  // YoY comparison — available when window is YTD
  const yoy = useMemo(() => {
    if (window !== 'ytd') return null;
    const now = Date.now();
    const year = new Date(now).getUTCFullYear();
    const dayOfYear = Math.floor((now - new Date(Date.UTC(year, 0, 1)).getTime()) / 86400000);
    const byYear = {};
    for (const t of realizedTrades) {
      if (!accountsByScope.has(t.account_id)) continue;
      const ts = new Date(t.trade_date || 0);
      const y = ts.getUTCFullYear();
      const yStart = new Date(Date.UTC(y, 0, 1)).getTime();
      const daysInYear = Math.floor((ts.getTime() - yStart) / 86400000);
      if (daysInYear > dayOfYear) continue; // only compare same time-of-year
      byYear[y] = (byYear[y] || 0) + (t.realized_pnl || 0);
    }
    const years = Object.keys(byYear).map(Number).sort((a, b) => b - a).slice(0, 3);
    return years.map(y => ({ year: y, total: byYear[y] }));
  }, [window, realizedTrades, accountsByScope]);

  // Per-account breakdown
  const perAccount = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      if (!map.has(t.account_id)) map.set(t.account_id, { id: t.account_id, total: 0, trades: 0, wins: 0, losses: 0 });
      const e = map.get(t.account_id);
      e.total += t.realized_pnl || 0;
      e.trades++;
      if ((t.realized_pnl || 0) > 0) e.wins++;
      if ((t.realized_pnl || 0) < 0) e.losses++;
    }
    return Array.from(map.values())
      .map(e => {
        const scored = e.wins + e.losses;
        const hitRate = scored > 0 ? e.wins / scored : null;
        const winsTotal = filtered.filter(t => t.account_id === e.id && (t.realized_pnl || 0) > 0).reduce((s, t) => s + t.realized_pnl, 0);
        const lossesTotal = filtered.filter(t => t.account_id === e.id && (t.realized_pnl || 0) < 0).reduce((s, t) => s + t.realized_pnl, 0);
        const avgWin = e.wins > 0 ? winsTotal / e.wins : null;
        const avgLoss = e.losses > 0 ? lossesTotal / e.losses : null;
        const expectancy = (hitRate != null && avgWin != null && avgLoss != null) ? hitRate * avgWin + (1 - hitRate) * avgLoss : null;
        return { ...e, hitRate, expectancy, account: accountById[e.id] };
      })
      .filter(e => e.account)
      .sort((a, b) => accountOrder(a.account.external_account_id) - accountOrder(b.account.external_account_id));
  }, [filtered, accountById]);

  // Monthly bar chart
  const monthly = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const d = new Date(t.trade_date || 0);
      if (isNaN(d)) continue;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      map.set(key, (map.get(key) || 0) + (t.realized_pnl || 0));
    }
    return Array.from(map.entries()).sort().map(([k, v]) => ({ key: k, total: v }));
  }, [filtered]);

  const maxMonthAbs = Math.max(1, ...monthly.map(m => Math.abs(m.total)));
  const perAccountSum = perAccount.reduce((s, a) => s + a.total, 0);
  const crossCheckDiff = Math.abs(perAccountSum - stats.total);
  const crossCheckOk = crossCheckDiff < 1;

  const isPaperScope = scope === 'algo_paper';

  return (
    <ModuleContainer title="Realized Performance" subtitle="Reflection / accounting view — dollar amounts here never leave this page">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">Scope:</span>
        <select value={scope} onChange={e => setScope(e.target.value)} className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white">
          {SCOPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider ml-2">Window:</span>
        <select value={window} onChange={e => setWindow(e.target.value)} className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white">
          {RP_WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {isPaperScope && (
          <span className="ml-auto text-[10px] font-mono text-korinth-warning">⚠️ Paper trading P&L — these trades were simulated, not real money.</span>
        )}
      </div>

      {stats.count === 0 ? (
        <div className="text-[11px] font-mono text-korinth-muted italic text-center py-6">
          No realized trades in {SCOPE_OPTIONS.find(s => s.value === scope)?.label || scope} for {RP_WINDOW_OPTIONS.find(w => w.value === window)?.label || window}.
          <div className="mt-2">
            Try widening the window, or switch to{' '}
            <button
              onClick={() => setScope(scope === 'discretionary' ? 'all' : 'discretionary')}
              className="text-korinth-accent hover:underline"
            >
              {scope === 'discretionary' ? 'All accounts' : 'Discretionary only'}
            </button>.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Hero stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-korinth-bg rounded p-3">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Total realized</div>
              <div className={`text-xl font-mono font-bold ${stats.total >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{fmtUsdP(stats.total)}</div>
              <div className="text-[9px] font-mono text-korinth-muted">{stats.count} trades</div>
            </div>
            <div className="bg-korinth-bg rounded p-3">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Hit rate</div>
              <div className="text-xl font-mono font-bold text-white">{stats.hitRate != null ? `${(stats.hitRate * 100).toFixed(1)}%` : '—'}</div>
              <div className="text-[9px] font-mono text-korinth-muted">{stats.wins}W / {stats.losses}L</div>
            </div>
            <div className="bg-korinth-bg rounded p-3">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Avg win / loss</div>
              <div className="text-sm font-mono">
                <span className="text-korinth-accent font-bold">{stats.avgWin != null ? fmtUsdP(stats.avgWin) : '—'}</span>
                <span className="text-korinth-muted"> / </span>
                <span className="text-korinth-danger font-bold">{stats.avgLoss != null ? fmtUsdP(stats.avgLoss) : '—'}</span>
              </div>
              <div className="text-[9px] font-mono text-korinth-muted">per trade</div>
            </div>
            <div className="bg-korinth-bg rounded p-3">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Expectancy</div>
              <div className={`text-xl font-mono font-bold ${(stats.expectancy || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{stats.expectancy != null ? fmtUsdP(stats.expectancy) : '—'}</div>
              <div className="text-[9px] font-mono text-korinth-muted">per trade</div>
            </div>
            <div className="bg-korinth-bg rounded p-3">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Best · Worst</div>
              <div className="text-[10px] font-mono">
                {stats.best && <div className="text-korinth-accent font-bold">{fmtUsdP(stats.best.realized_pnl)} <span className="text-korinth-muted">({stats.best.symbol}, {fmtDate(stats.best.trade_date)})</span></div>}
                {stats.worst && stats.worst !== stats.best && <div className="text-korinth-danger font-bold">{fmtUsdP(stats.worst.realized_pnl)} <span className="text-korinth-muted">({stats.worst.symbol}, {fmtDate(stats.worst.trade_date)})</span></div>}
              </div>
            </div>
          </div>

          {/* YoY strip */}
          {yoy && yoy.length > 1 && (
            <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded p-3">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Year-over-year (same time of year)</div>
              <div className="flex items-center gap-6 flex-wrap text-[11px] font-mono">
                {yoy.map((y, i) => {
                  const prev = yoy[i + 1];
                  const change = prev ? y.total - prev.total : null;
                  return (
                    <div key={y.year}>
                      <div className="text-[9px] text-korinth-muted">{y.year} YTD</div>
                      <div className="flex items-center gap-1.5">
                        <span className={y.total >= 0 ? 'text-korinth-accent font-bold' : 'text-korinth-danger font-bold'}>{fmtUsdP(y.total)}</span>
                        {change != null && (
                          change >= 0
                            ? <TrendingUp className="w-3 h-3 text-korinth-accent" />
                            : <TrendingDown className="w-3 h-3 text-korinth-danger" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-account breakdown */}
          <div>
            <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Per-account breakdown</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px] font-mono">
                <thead>
                  <tr className="text-korinth-muted border-b border-korinth-border/30">
                    <th className="text-left py-1 px-2">Account</th>
                    <th className="text-right py-1 px-2">Trades</th>
                    <th className="text-right py-1 px-2">W / L</th>
                    <th className="text-right py-1 px-2">Hit rate</th>
                    <th className="text-right py-1 px-2">Total</th>
                    <th className="text-right py-1 px-2">Expectancy</th>
                  </tr>
                </thead>
                <tbody>
                  {perAccount.map(e => (
                    <tr key={e.id} className="border-b border-korinth-border/10">
                      <td className="py-1.5 px-2 text-white font-bold">{e.account.broker} · {accountLabel(e.account)}</td>
                      <td className="py-1.5 px-2 text-right text-korinth-text">{e.trades}</td>
                      <td className="py-1.5 px-2 text-right text-korinth-text">{e.wins}/{e.losses}</td>
                      <td className="py-1.5 px-2 text-right text-korinth-text">{e.hitRate != null ? `${(e.hitRate * 100).toFixed(0)}%` : '—'}</td>
                      <td className={`py-1.5 px-2 text-right ${e.total >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{fmtUsdP(e.total)}</td>
                      <td className={`py-1.5 px-2 text-right ${(e.expectancy || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{e.expectancy != null ? fmtUsdP(e.expectancy) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly bar chart */}
          {monthly.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Monthly P&L</div>
              <div className="flex items-end gap-1 h-32 bg-korinth-bg/30 rounded p-2 overflow-x-auto">
                {monthly.map(m => {
                  const heightPct = (Math.abs(m.total) / maxMonthAbs) * 45; // 45% max per side
                  const color = m.total >= 0 ? '#10b981' : '#ef4444';
                  return (
                    <div key={m.key} className="flex flex-col items-center flex-shrink-0" title={`${m.key}: ${fmtUsdP(m.total)}`}>
                      <div className="flex items-center justify-center h-20 relative">
                        {m.total >= 0 ? (
                          <div style={{ height: `${heightPct}%`, backgroundColor: color, width: 10 }} className="self-end" />
                        ) : null}
                      </div>
                      <div className="w-0.5 h-px bg-korinth-muted/30" />
                      <div className="flex items-start h-12 relative">
                        {m.total < 0 ? (
                          <div style={{ height: `${heightPct}%`, backgroundColor: color, width: 10 }} className="self-start" />
                        ) : null}
                      </div>
                      <div className="text-[8px] font-mono text-korinth-muted mt-0.5 whitespace-nowrap">{m.key.slice(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cross-check */}
          <div className={`text-[9px] font-mono ${crossCheckOk ? 'text-korinth-muted' : 'text-korinth-warning'}`}>
            {crossCheckOk
              ? `∑ per-account = ${fmtUsdP(perAccountSum)} · matches hero ✓`
              : `⚠️ Cross-check: per-account sum ${fmtUsdP(perAccountSum)} ≠ hero ${fmtUsdP(stats.total)} (diff ${fmtUsdP(crossCheckDiff)})`}
          </div>
        </div>
      )}
    </ModuleContainer>
  );
}

// ─── Sprint 21.7: Exposure + Macro Context + Position Thesis prompt ────

/**
 * ExposureBlock — renders sector bar + theme breakdown + geography + market cap
 * from /api/kairos/personal-context.
 *
 * P17-safe: consumes only the sanitized personal-context endpoint. No $.
 */
function ExposureBlock({ context }) {
  if (!context) return null;
  const { exposure } = context;
  if (!exposure) return null;

  // Fixed sector color palette (11 GICS sectors + 'Unclassified')
  const SECTOR_COLORS = {
    'Information Technology': '#3b82f6',
    'Health Care': '#10b981',
    'Financials': '#8b5cf6',
    'Energy': '#f97316',
    'Industrials': '#6366f1',
    'Materials': '#a16207',
    'Consumer Discretionary': '#ec4899',
    'Consumer Staples': '#14b8a6',
    'Utilities': '#64748b',
    'Real Estate': '#d97706',
    'Communication Services': '#0ea5e9',
    'Unclassified': '#475569',
  };
  const colorFor = (s) => SECTOR_COLORS[s] || '#64748b';

  const sectors = (exposure.sectors || []).filter(s => s.pct > 0);
  const totalSectorPct = sectors.reduce((s, x) => s + x.pct, 0) || 1;
  const themes = (exposure.themes || []).slice(0, 8);
  const geo = (exposure.geography || []).slice(0, 5);
  const mc = exposure.market_cap || [];

  return (
    <ModuleContainer title="EXPOSURE" subtitle="Discretionary only · GICS sectors, themes, geography, market cap">
      <div className="space-y-4">
        {/* Sector stacked bar */}
        <div>
          <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Sectors</div>
          <div className="flex w-full h-5 rounded overflow-hidden border border-korinth-border">
            {sectors.map(s => (
              <div key={s.name} title={`${s.name} · ${s.pct.toFixed(1)}%`}
                style={{ width: `${(s.pct / totalSectorPct) * 100}%`, background: colorFor(s.name) }} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {sectors.map(s => (
              <div key={s.name} className="flex items-center gap-1.5 text-[10px] font-mono">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colorFor(s.name) }} />
                <span className="text-korinth-text">{s.name}</span>
                <span className="text-korinth-muted">{s.pct.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Themes breakdown */}
        {themes.length > 0 && (
          <div>
            <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Themes (top 8)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {themes.map(th => (
                <div key={th.name} className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                  <span className="text-white font-bold flex-shrink-0 w-40 truncate">{th.name}</span>
                  <span className="text-korinth-accent font-bold">{th.pct.toFixed(1)}%</span>
                  <span className="text-korinth-muted truncate flex-1">{(th.tickers || []).join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Geography + Market cap */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {geo.length > 0 && (
            <div>
              <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Geography</div>
              <div className="space-y-1">
                {geo.map(g => (
                  <div key={g.country} className="flex items-center gap-2 text-[10px] font-mono">
                    <span className="text-white font-bold w-10">{g.country || '??'}</span>
                    <div className="flex-1 h-1.5 bg-korinth-border/30 rounded overflow-hidden">
                      <div className="h-full bg-korinth-accent" style={{ width: `${Math.min(100, g.pct)}%` }} />
                    </div>
                    <span className="text-korinth-muted w-14 text-right">{g.pct.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {mc.length > 0 && (
            <div>
              <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Market cap</div>
              <div className="space-y-1">
                {mc.map(m => (
                  <div key={m.tier} className="flex items-center gap-2 text-[10px] font-mono">
                    <span className="text-white font-bold w-14 capitalize">{m.tier}</span>
                    <div className="flex-1 h-1.5 bg-korinth-border/30 rounded overflow-hidden">
                      <div className="h-full" style={{ width: `${Math.min(100, m.pct)}%`, background: '#8b5cf6' }} />
                    </div>
                    <span className="text-korinth-muted w-14 text-right">{m.pct.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ModuleContainer>
  );
}

/**
 * MacroContextBlock — cash buffer, drawdown direction, beta, deploy capacity.
 */
function MacroContextBlock({ context }) {
  if (!context?.macro_context) return null;
  const mc = context.macro_context;
  const capacityColor = { high: '#10b981', medium: '#f59e0b', low: '#ef4444' }[mc.capacity_to_deploy] || '#64748b';
  const drawdownColor = mc.unrealized_drawdown_pct >= 0 ? '#10b981' : mc.unrealized_drawdown_pct <= -10 ? '#ef4444' : '#f59e0b';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Cash buffer</div>
        <div className="text-lg font-mono font-bold text-white">{mc.cash_buffer_pct != null ? mc.cash_buffer_pct.toFixed(1) + '%' : '—'}</div>
      </div>
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Unrealized P&L</div>
        <div className="text-lg font-mono font-bold" style={{ color: drawdownColor }}>
          {mc.unrealized_drawdown_pct != null ? `${mc.unrealized_drawdown_pct >= 0 ? '+' : ''}${mc.unrealized_drawdown_pct.toFixed(1)}%` : '—'}
        </div>
      </div>
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Portfolio β vs SPY</div>
        {mc.portfolio_beta_spx != null ? (
          <>
            <div className="text-lg font-mono font-bold text-white">{mc.portfolio_beta_spx.toFixed(2)}x</div>
            {!mc.beta_data_complete && (
              <div className="text-[8px] font-mono text-korinth-warning mt-0.5" title={JSON.stringify(mc.beta_excluded_reasons || {})}>
                {(mc.tickers_excluded_from_beta || []).length} excluded
              </div>
            )}
          </>
        ) : (
          <div className="text-sm font-mono text-korinth-muted italic">not computed</div>
        )}
      </div>
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Deploy capacity</div>
        <div className="text-lg font-mono font-bold capitalize" style={{ color: capacityColor }}>{mc.capacity_to_deploy || '—'}</div>
      </div>
    </div>
  );
}

const ENTRY_REASONS = [
  { value: 'catalyst',  label: 'Catalyst (earnings / FDA / event)' },
  { value: 'technical', label: 'Technical (chart / level / setup)' },
  { value: 'theme',     label: 'Theme (AI / Energy / Macro thesis)' },
  { value: 'news',      label: 'News (breaking development)' },
  { value: 'swing',     label: 'Swing (momentum / trend following)' },
  { value: 'other',     label: 'Other' },
];

/**
 * PositionThesisPrompt — for each position in plutus_positions without an
 * active position_thesis, shows a single-row prompt asking Kamal why he
 * entered. Writes encrypted notes via /api/plutus/thesis.
 */
function PositionThesisPrompt() {
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null); // key: account_id::ticker
  const [form, setForm] = useState({ entry_reason: 'catalyst', notes: '', target_price: '', target_exit_date: '' });
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/api/plutus/thesis/missing');
      if (r.ok) { const d = await r.json(); setMissing(d.missing || []); }
    } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save(row) {
    setSaving(true);
    try {
      await fetch('/api/plutus/thesis', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: row.account_id, ticker: row.ticker, ...form }),
      });
      setOpenForm(null);
      setForm({ entry_reason: 'catalyst', notes: '', target_price: '', target_exit_date: '' });
      await load();
    } catch {}
    setSaving(false);
  }

  async function skip(row) {
    setSaving(true);
    try {
      await fetch('/api/plutus/thesis/dismiss-new', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: row.account_id, ticker: row.ticker }),
      });
      await load();
    } catch {}
    setSaving(false);
  }

  if (loading || missing.length === 0) return null;

  return (
    <ModuleContainer title="CAPTURE POSITION THESIS" subtitle={`${missing.length} position${missing.length === 1 ? '' : 's'} without a recorded thesis — why did you enter?`}>
      <div className="space-y-2">
        {missing.map(row => {
          const key = `${row.account_id}::${row.ticker}`;
          const isOpen = openForm === key;
          return (
            <div key={key} className="border border-korinth-border rounded-lg">
              <div className="flex items-center gap-3 px-3 py-2 bg-korinth-bg">
                <span className="text-sm font-mono font-bold text-white w-16">{row.ticker}</span>
                <span className="text-[10px] font-mono text-korinth-muted">{row.external_account_id} · {row.portfolio_weight_pct?.toFixed(1)}%</span>
                <div className="ml-auto flex gap-2">
                  {!isOpen && <button onClick={() => setOpenForm(key)} className="text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">Capture thesis</button>}
                  <button onClick={() => skip(row)} disabled={saving} className="text-[10px] font-mono px-3 py-1 rounded border border-korinth-border text-korinth-muted hover:text-korinth-text disabled:opacity-50">Skip for now</button>
                </div>
              </div>
              {isOpen && (
                <div className="p-3 space-y-2 bg-korinth-surface">
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-mono text-korinth-muted uppercase">Entry reason</label>
                    <select value={form.entry_reason} onChange={e => setForm(f => ({ ...f, entry_reason: e.target.value }))}
                      className="text-[11px] font-mono bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-white">
                      {ENTRY_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-mono text-korinth-muted uppercase">Notes (encrypted · optional)</label>
                    <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      rows={3} placeholder="Catalyst thesis, target setup, trigger, anything you'll want to remember..."
                      className="text-[11px] font-mono bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                      <label className="text-[9px] font-mono text-korinth-muted uppercase">Target price (optional)</label>
                      <input type="number" step="0.01" value={form.target_price} onChange={e => setForm(f => ({ ...f, target_price: e.target.value }))}
                        className="text-[11px] font-mono bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-white" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[9px] font-mono text-korinth-muted uppercase">Target exit date (optional)</label>
                      <input type="date" value={form.target_exit_date} onChange={e => setForm(f => ({ ...f, target_exit_date: e.target.value }))}
                        className="text-[11px] font-mono bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-white" />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setOpenForm(null)} className="text-[10px] font-mono px-3 py-1 rounded border border-korinth-border text-korinth-muted hover:text-korinth-text">Cancel</button>
                    <button onClick={() => save(row)} disabled={saving} className="text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent text-korinth-bg font-bold disabled:opacity-50">Save thesis</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModuleContainer>
  );
}

// ─── Main content ──────────────────────────────────────────────────────

function HoldingsContent({ onLock }) {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [realizedTrades, setRealizedTrades] = useState([]);
  const [personalContext, setPersonalContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [filter, setFilter] = useState('all');
  // Sprint 22.5a.8: surface sync result feedback (skipped/error/ok) to user.
  // Pre-22.5a.8 manual click was silent — user couldn't tell whether the
  // sync ran, was rate-limited, or failed. Auto-clears after 12 seconds so
  // the UI doesn't accumulate stale toasts.
  const [syncToast, setSyncToast] = useState(null); // { broker, status, message, retry_after_seconds? }

  async function load() {
    setLoading(true);
    try {
      // Window 1y for per-account Trade History default; Realized section loads
      // its own full dataset separately so lifetime metrics always available
      const [ovRes, alRes, rtRes, pcRes] = await Promise.all([
        fetch('/api/plutus/overview?window=1y'),
        fetch('/api/plutus/alerts'),
        fetch('/api/plutus/realized-trades'),
        fetch('/api/kairos/personal-context'),
      ]);
      if (ovRes.status === 423) {
        sessionStorage.removeItem(UNLOCK_FLAG);
        onLock();
        return;
      }
      if (ovRes.ok) setData(await ovRes.json());
      if (alRes.ok) { const d = await alRes.json(); setAlerts(d.alerts || []); }
      if (rtRes.ok) { const d = await rtRes.json(); setRealizedTrades(d.trades || []); }
      if (pcRes.ok) setPersonalContext(await pcRes.json());
    } catch {}
    setLoading(false);
  }

  // Sprint 22.5a.8 Fix 1+2: pass force=true to bypass cooldown on manual
  // user clicks (the 5-min cooldown is to protect cron from hammering
  // IBKR; explicit user retry is "I want to try now, I accept the
  // consequences"). Capture response shape and render toast feedback
  // covering ok/skipped/error so silent fails don't recur.
  async function sync(broker) {
    setSyncing(broker);
    setSyncToast(null);
    try {
      const res = await fetch(`/api/plutus/sync/${broker}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: true }),
      });
      let body = {};
      try { body = await res.json(); } catch {}
      if (body.ok) {
        setSyncToast({ broker, status: 'ok', message: `Sync OK (${body.positions ?? 0} positions, ${body.trades ?? 0} trades)` });
      } else if (body.skipped) {
        const retryAfter = Number.isFinite(body.retry_after_seconds) ? body.retry_after_seconds : null;
        const retryMsg = retryAfter !== null ? ` · next retry available in ${Math.ceil(retryAfter / 60)} min` : '';
        setSyncToast({ broker, status: 'skipped', message: `Sync skipped: ${body.reason || 'unknown'}${retryMsg}`, retry_after_seconds: retryAfter });
      } else if (body.error) {
        setSyncToast({ broker, status: 'error', message: `Sync failed: ${String(body.error).slice(0, 200)}` });
      } else if (!res.ok) {
        setSyncToast({ broker, status: 'error', message: `Sync HTTP ${res.status}` });
      }
      await load();
    } catch (e) {
      setSyncToast({ broker, status: 'error', message: `Sync request failed: ${e?.message || 'network'}` });
    }
    setSyncing(null);
    // Auto-clear toast after 12s so old messages don't linger
    setTimeout(() => setSyncToast(t => (t && t.broker === broker) ? null : t), 12000);
  }

  async function lock() {
    try { await fetch('/api/plutus/lock', { method: 'POST' }); } catch {}
    sessionStorage.removeItem(UNLOCK_FLAG);
    onLock();
  }

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    const { accounts, latestNav, positions, trades } = data;
    const positionsByAcc = {}, tradesByAcc = {};
    for (const p of positions || []) (positionsByAcc[p.account_id || 'unknown'] = positionsByAcc[p.account_id || 'unknown'] || []).push(p);
    for (const t of trades || []) (tradesByAcc[t.account_id || 'unknown'] = tradesByAcc[t.account_id || 'unknown'] || []).push(t);
    for (const k of Object.keys(tradesByAcc)) {
      tradesByAcc[k].sort((a, b) => (new Date(b.trade_date || 0).getTime()) - (new Date(a.trade_date || 0).getTime()));
    }
    const list = (accounts || []).map(acc => ({
      account: acc,
      nav: latestNav?.find(n => n.account_id === acc.id) || null,
      positions: positionsByAcc[acc.id] || [],
      trades: tradesByAcc[acc.id] || [],
      cashLabel: acc.broker === 'Binance' ? 'Stablecoins' : 'Cash',
    }));
    list.sort((a, b) => accountOrder(a.account.external_account_id) - accountOrder(b.account.external_account_id));
    return list;
  }, [data]);

  if (loading && !data) return <div className="text-[11px] font-mono text-korinth-muted text-center py-12">Loading holdings...</div>;
  if (!data) return <div className="text-[11px] font-mono text-korinth-danger text-center py-12">Failed to load.</div>;

  const totalNav = groups.reduce((s, g) => s + (g.nav?.nav_usd || 0), 0);
  const totalCash = groups.reduce((s, g) => s + (g.nav?.cash_usd || 0), 0);
  const totalUnrealized = groups.reduce((s, g) => s + g.positions.filter(p => (p.quantity || 0) > 0).reduce((ps, p) => ps + (p.unrealized_pnl || 0), 0), 0);
  const allOpen = groups.flatMap(g => g.positions.filter(p => (p.quantity || 0) > 0));

  const visibleGroups = filter === 'all' ? groups : groups.filter(g => String(g.account.id) === filter);

  return (
    <div className="space-y-6">
      {/* Alerts strip */}
      <AlertsStrip alerts={alerts} />

      {/* Total NAV hero */}
      <div className="bg-korinth-surface border-l-4 border-l-korinth-accent border border-korinth-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-korinth-accent" />
          <span className="text-xs font-mono font-bold uppercase tracking-wider text-korinth-accent">Total NAV · All Accounts</span>
          <button onClick={lock} className="ml-auto flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-korinth-border hover:border-korinth-danger/50 hover:text-korinth-danger">
            <Lock className="w-3 h-3" /> Lock
          </button>
        </div>
        <div className="flex items-baseline gap-4 flex-wrap"><span className="text-3xl font-mono font-bold text-white">{fmtUsd(totalNav)}</span></div>
        <div className="flex gap-4 mt-2 text-[10px] font-mono text-korinth-muted flex-wrap">
          <span>Cash + Stablecoins: <span className="text-korinth-text">{fmtUsd(totalCash)}</span></span>
          <span>Unrealized P&L: <span className={totalUnrealized >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>{fmtUsdP(totalUnrealized)}</span></span>
          {data?.summary?.realized_ytd_decrypted != null && (
            <span>Realized YTD: <span className={data.summary.realized_ytd_decrypted > 0 ? 'text-korinth-accent font-bold' : data.summary.realized_ytd_decrypted < 0 ? 'text-korinth-danger font-bold' : 'text-korinth-muted font-bold'}>{fmtUsdP(data.summary.realized_ytd_decrypted)}</span></span>
          )}
          <span>Accounts: <span className="text-korinth-text">{groups.length}</span></span>
          <span>Open positions: <span className="text-korinth-text">{allOpen.length}</span></span>
        </div>
      </div>

      {/* Sprint 21.7: Exposure + Macro Context (informational — no alerts fire) */}
      <MacroContextBlock context={personalContext} />
      <ExposureBlock context={personalContext} />

      {/* Sprint 21.7: position thesis capture prompt */}
      <PositionThesisPrompt />

      {/* Accounts strip — sorted per Sprint 21.6d correct order */}
      <ModuleContainer title="Accounts" subtitle={`${groups.length} connected · sorted Kamal → Binance → algo subs`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map(g => <AccountCard key={g.account.id} account={g.account} nav={g.nav} onSync={sync} syncing={syncing} />)}
        </div>
      </ModuleContainer>

      {/* Filter bar */}
      {groups.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">View:</span>
          <button onClick={() => setFilter('all')} className={`text-[10px] font-mono px-3 py-1.5 rounded border ${filter === 'all' ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}>
            All ({allOpen.length})
          </button>
          {groups.map(g => (
            <button key={g.account.id} onClick={() => setFilter(String(g.account.id))}
              className={`text-[10px] font-mono px-3 py-1.5 rounded border ${filter === String(g.account.id) ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}>
              {g.account.broker} · {accountLabel(g.account)} ({g.positions.filter(p => (p.quantity || 0) > 0).length})
            </button>
          ))}
        </div>
      )}

      {/* Per-account sections with collapsible Trade History */}
      <div className="space-y-8">
        {visibleGroups.length === 0 ? (
          <div className="text-[11px] font-mono text-korinth-muted text-center py-6 italic">
            No accounts yet. Configure env + trigger sync.
          </div>
        ) : (
          visibleGroups.map(g => <AccountSection key={g.account.id} group={g} />)
        )}
      </div>

      {/* Realized Performance (scope + window filters, rich stats) */}
      <RealizedPerformance accounts={data.accounts || []} realizedTrades={realizedTrades} />

      {/* Sprint 21.6g: Sync Health footer — moved from Settings.
          Sprint 22.5a.8: syncToast prop surfaces ok/skipped/error result
          feedback inline (was silent pre-22.5a.8). */}
      <SyncHealthFooter groups={groups} onSync={sync} syncing={syncing} syncToast={syncToast} />

      {/* P17 disclosure */}
      <div className="text-[9px] font-mono text-korinth-muted/60 leading-relaxed border-t border-korinth-border/30 pt-3">
        <Eye className="w-2.5 h-2.5 inline mr-1" />
        P17 · PRIVATE tier. Dollar amounts on this page are never sent to Telegram, Mnemosyne, server logs, or KAIROS prompts.
      </div>
    </div>
  );
}

// ─── Sprint 21.6g: Sync Health footer ──────────────────────────────────
// Compact summary of per-account sync state + Sync now buttons. Moved here
// from Settings where it was cluttering the configuration page.
// Sprint 22.5a.8: syncToast prop surfaces ok/skipped/error feedback inline.
function SyncHealthFooter({ groups, onSync, syncing, syncToast }) {
  const now = Date.now();
  const toastBg = syncToast?.status === 'ok'
    ? 'bg-korinth-accent/15 text-korinth-accent border-korinth-accent/40'
    : syncToast?.status === 'skipped'
      ? 'bg-amber-500/15 text-amber-400 border-amber-500/40'
      : 'bg-korinth-danger/15 text-korinth-danger border-korinth-danger/40';
  return (
    <ModuleContainer title="Sync Health" subtitle="Per-account last sync + manual trigger">
      {syncToast && (
        <div className={`text-[10px] font-mono px-2 py-1 mb-2 rounded border ${toastBg}`}>
          [{(syncToast.broker || 'sync').toUpperCase()}] {syncToast.message}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead>
            <tr className="text-korinth-muted border-b border-korinth-border/30">
              <th className="text-left py-1 px-2">Account</th>
              <th className="text-left py-1 px-2">Status</th>
              <th className="text-left py-1 px-2">Last sync</th>
              {/* Sprint 22.6.1 (ADR-088 EXTENDED): attempt-tracking column.
                  "Last sync" stays success-only ("when did fresh data
                  arrive?"); "Last attempt" answers "are we still trying?"
                  Critical when IBKR throttles for 24-72h with persistent
                  errors — the attempt timestamp shows operator we ARE
                  retrying, IBKR keeps refusing. */}
              <th className="text-left py-1 px-2">Last attempt</th>
              <th className="text-left py-1 px-2">Next</th>
              <th className="text-left py-1 px-2">Error</th>
              <th className="text-right py-1 px-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => {
              const acc = g.account;
              const isBinance = acc.broker === 'Binance';
              const noQuery = acc.sync_status === 'no_query_configured';
              const label = accountLabel(acc);
              const ageH = acc.last_sync_at ? Math.floor((now - new Date(acc.last_sync_at).getTime()) / 3600000) : null;
              const statusColor = acc.sync_status === 'ok' ? '#10b981'
                : acc.sync_status === 'error' ? '#ef4444'
                : noQuery ? '#6b7280'
                : '#f59e0b';
              return (
                <tr key={acc.id} className="border-b border-korinth-border/10">
                  <td className="py-1.5 px-2">
                    <span className="text-white font-bold">{acc.broker}</span>
                    <span className="text-korinth-muted"> · </span>
                    <span className="text-korinth-text">{label}</span>
                  </td>
                  <td className="py-1.5 px-2">
                    <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: statusColor, backgroundColor: statusColor + '15' }}>
                      {noQuery ? 'no Flex query' : (acc.sync_status || 'pending')}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-korinth-muted">
                    {acc.last_sync_at ? (
                      <>
                        {acc.last_sync_at.slice(5, 16)}
                        {ageH != null && <span className="text-[9px] ml-1">({ageH}h ago)</span>}
                      </>
                    ) : '—'}
                  </td>
                  {/* Sprint 22.6.1: Last attempt column. Shows attempt
                      timestamp + colored icon when most recent attempt
                      failed AFTER last successful sync. Tooltip carries
                      the full last_attempted_error string. */}
                  <td className="py-1.5 px-2">
                    {(() => {
                      const lastAttempt = acc.last_attempted_at;
                      if (!lastAttempt) return <span className="text-korinth-muted">—</span>;
                      const attemptStatus = acc.last_attempted_status;
                      const ageMin = Math.floor((now - new Date(lastAttempt).getTime()) / 60000);
                      const ageStr = ageMin < 60 ? `${ageMin}m ago` : ageMin < 1440 ? `${Math.floor(ageMin / 60)}h ago` : `${Math.floor(ageMin / 1440)}d ago`;
                      const lastSyncTs = acc.last_sync_at ? new Date(acc.last_sync_at).getTime() : 0;
                      const failedAfterLastSync = attemptStatus === 'error' && new Date(lastAttempt).getTime() > lastSyncTs;
                      const inProgress = attemptStatus === 'in_progress';
                      const color = inProgress ? '#f59e0b' : failedAfterLastSync ? '#ef4444' : '#6b7280';
                      const dot = inProgress ? '●' : failedAfterLastSync ? '✕' : '·';
                      const tooltip = inProgress ? 'sync in progress' : failedAfterLastSync ? `failed: ${acc.last_attempted_error || 'unknown error'}` : `attempt status: ${attemptStatus || 'unknown'}`;
                      return (
                        <span title={tooltip}>
                          <span style={{ color, fontWeight: 'bold', marginRight: '4px' }}>{dot}</span>
                          <span className="text-korinth-muted">{lastAttempt.slice(11, 16)}</span>
                          <span className="text-[9px] ml-1 text-korinth-muted">({ageStr})</span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-1.5 px-2 text-korinth-muted">
                    {noQuery ? '—' : isBinance ? 'every 4h' : 'daily 06:00 UTC'}
                  </td>
                  <td className="py-1.5 px-2 text-korinth-danger">
                    {acc.sync_error ? <span title={acc.sync_error}>{acc.sync_error.slice(0, 40)}</span> : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <button
                      onClick={() => onSync(isBinance ? 'binance' : 'ibkr')}
                      disabled={noQuery || syncing === (isBinance ? 'binance' : 'ibkr')}
                      title={noQuery ? 'Add IBKR_FLEX_QUERY_ID_<NAME> to backend/.env to enable sync' : ''}
                      className="text-[9px] font-mono px-2 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {syncing === (isBinance ? 'binance' : 'ibkr') ? 'Syncing…' : noQuery ? 'No query' : 'Sync now'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ModuleContainer>
  );
}

export default function HoldingsPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('holdings'); }, []);

  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(UNLOCK_FLAG) === 'true');
  const [hasPin, setHasPin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function check() {
      try {
        const r = await fetch('/api/plutus/has-pin');
        if (r.ok) {
          const d = await r.json();
          setHasPin(d.pin_set);
          if (!d.unlocked && unlocked) { sessionStorage.removeItem(UNLOCK_FLAG); setUnlocked(false); }
          if (d.unlocked) setUnlocked(true);
        }
      } catch {}
      setChecking(false);
    }
    check();
  }, []);

  if (checking) return <div className="text-[11px] font-mono text-korinth-muted text-center py-12">Checking PLUTUS status...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        {unlocked ? <Unlock className="w-6 h-6 text-korinth-accent" /> : <Lock className="w-6 h-6 text-korinth-muted" />}
        <h1 className="text-2xl font-bold text-white">Holdings</h1>
        <span className="text-korinth-muted text-sm font-mono">PLUTUS · Private Wealth Steward</span>
      </div>

      {unlocked ? (
        <HoldingsContent onLock={() => setUnlocked(false)} />
      ) : (
        <PinGate hasPinSet={hasPin === true} onUnlocked={() => setUnlocked(true)} />
      )}
    </div>
  );
}
